import { IMoment, moment } from '../lib/moment';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import countBy from 'lodash/countBy';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';

import { Session } from './session';
import { ServerApi } from './server-api';
import { ServiceWorkerDynamicConfigService } from './dynamic-configs/bulk-service-worker-config';

import { extractResponseBody, kmToMiles } from '../lib/utils';
import { ALL_ACCESSIBLE_GROUP_SCOPE, PERMISSIONS } from './../lib/permissions';

interface IAssetSearchServerResponse {
  assets: IAssetSearchServerResponseContainer[];
}

interface IAssetSearchServerResponseContainer {
  asset: IAssetServer;
}

export interface IAssetServer {
  id: number;
  name: string;
  status: string;
  updated_at: string;
}

export interface IAsset {
  name: string;
  value: number;
  showAssetEngineHoursError?: boolean;
  groupIds?: number[]; // TODO: This will not be optional once BE support gets added.
  existingSchedule?: {
    frequencyType: string;
    frequencyValue: number | string;
    maintenanceName: string;
    maintenanceType: string;
  };
}

export interface IMaintenanceAsset extends IAsset {
  year?: string;
  make?: string;
  model?: string;
  odometer?: number;
  engineHours?: number;
  cableConnected?: boolean;
  name: string;
  value: number;
  source?: string;
  isItOEMEntity?: boolean;
  groupIds: number[];
  updatedAt: string;
  status: string;
  metric_units: boolean;
  _sortNumber: string;
  type?: string;
  existingReminders?: {
    [key: string]: IAssetMaintenanceReminder;
  };
}

export interface IAssetMaintenanceReminder {
  maintenance_name: string;
  maintenance_type: string;
  frequency_type: string;
  frequency_value: number;
}
interface IAssetServerResponse {
  status: string;
  maintenance_reminder?: IAssetMaintenanceReminder;
  name: string;
  id: number;
  group_ids: number[];
  make: string;
  model: string;
  year: string;
  cable_connected: boolean;
  engine_hours: number;
  odometer: number;
  updated_at: string;
}

export interface IAssetList {
  list: IAsset[];
}

export interface IMaintenanceAssetList {
  list: IMaintenanceAsset[];
}

export interface IAssetCount {
  active: number;
  deactivated: number;
  total: number;
}

export type ParamsMapKeys = 'group_ids' | 'updated_after' | 'with_camera';
export type ParamsMapValues = 'updated_after' | string;

@Injectable({ providedIn: 'root' })
export class AssetsSearch {
  private cachedList: IAssetServer[] = [];
  private lastUpdatedAt = null;

  constructor(
    private api: ServerApi,
    private session: Session,
    private serviceWorkerDynamicConfigService: ServiceWorkerDynamicConfigService,
  ) {}

  public getList(
    preventGroupFiltering: boolean = false,
    excludeDeactivated: boolean = false,
    maintenanceReminder: boolean = false,
    permissionToCheckGroupVisibility: PERMISSIONS = PERMISSIONS.NONE,
    existingReminders: boolean = false,
  ): Observable<IAssetList> {
    return this.serverRequest(maintenanceReminder)
      .pipe(
        map(this.mapList.bind(
          this,
          preventGroupFiltering,
          excludeDeactivated,
          maintenanceReminder,
          existingReminders,
          permissionToCheckGroupVisibility,
        )),
      );
  }

  public getByIds(ids: string[] | number[]): Observable<IAssetServer[]> {
    return this.serverRequest().pipe(
      map((list: IAssetServer[]) => {
        return list.filter((v: IAssetServer) => includes(ids, v.id));
      }),
    );
  }

  public getByName(name: string): Observable<IAssetServer> {
    return this.serverRequest().pipe(
      map((list: IAssetServer[]) => {
        return list.find((v: IAssetServer) => v.name === name);
      }),
    );
  }

  public getCount(): Observable<IAssetCount> {
    return this.serverRequest().pipe(map((list: IAssetServer[]) => {
      const count: IAssetCount = countBy(list, 'status');
      return {
        active: count.active || 0,
        deactivated: count.deactivated || 0,
        total: list.length,
      };
    }));
  }

  protected getParams(maintenanceReminder: boolean = false): Map<ParamsMapKeys, ParamsMapValues> {
    const params: Map<ParamsMapKeys, ParamsMapValues> = new Map<ParamsMapKeys, ParamsMapValues>();
    params.set('group_ids', null); // Prevent group_ids param from being sent
    if (this.lastUpdatedAt) {
      params.set('updated_after', this.lastUpdatedAt.formatToServerUtc());
    }
    return params;
  }

  private updateCachedList(res: IAssetSearchServerResponse): IAssetServer[] {
    const newList: IAssetSearchServerResponseContainer[] = res.assets;
    const cachedList: IAssetServer[] = this.cachedList;
    newList.forEach((a: IAssetSearchServerResponseContainer) => {
      const asset: IAssetServer = a.asset;
      const index: number = cachedList.findIndex(v => v.id === asset.id);
      if (index >= 0) cachedList.splice(index, 1, asset);
      else cachedList.push(asset);
    });
    return cachedList;
  }

  private serverRequest(maintenanceReminder = false): Observable<IAssetServer[]> {
    const params: Map<ParamsMapKeys, ParamsMapValues> = this.getParams(maintenanceReminder);
    const apiUrl: string = maintenanceReminder ? 'assets/maintenance/bulk?include_maintenance_reminder=true' : 'assets/bulk';

    return this.api
      .get(
        apiUrl,
        params,
        maintenanceReminder ? this.api.API_VER_W3 : this.api.API_VER_W2,
      )
      .pipe(
        tap(() => (this.lastUpdatedAt = moment())),
        map((response: HttpResponse<IAssetSearchServerResponse>) =>
          extractResponseBody<IAssetSearchServerResponse>(response),
        ),
        map(this.updateCachedList.bind(this)),
      );
  }

  public mapList(
    preventGroupFiltering: boolean,
    excludeDeactivated: boolean,
    maintenanceReminder: boolean,
    existingReminders: boolean = false,
    permissionToCheckGroupVisibility: PERMISSIONS,
    list: IAssetServer[],
  ): IAssetList {
    const session: Session = this.session;
    const metricUnits: boolean = session.getUserMetricUnits();
    let assets: IAsset[] =
      filter(
        list,
        a =>
          preventGroupFiltering ||
          session.isInSelectedGroups(a.group_ids, permissionToCheckGroupVisibility || ALL_ACCESSIBLE_GROUP_SCOPE),
      )
        .filter((a: IAssetServerResponse) => !excludeDeactivated || a.status === 'active')
        .map((asset: IAssetServerResponse) => {
          const deactivated: string = asset.status === 'deactivated' ? $localize`(deactivated)` : '';
          const reminder: IAssetMaintenanceReminder = asset.maintenance_reminder;
          const cableConnected = asset.cable_connected || false;
          return {
            name: compact([asset.name, deactivated]).join(' '),
            value: asset.id,
            ...maintenanceReminder && {
              groupIds: asset.group_ids || [],
              make: asset.make,
              model: asset.model,
              year: asset.year,
              cableConnected,
              engineHours: asset.engine_hours || null,
              showAssetEngineHoursError: cableConnected == true ? asset.engine_hours == null ? true : false : true,
              odometer: (asset.odometer && !metricUnits) ? kmToMiles(asset.odometer) : asset.odometer,
              updatedAt: asset.updated_at,
              existingSchedule: (reminder && !existingReminders) ? {
                maintenanceName: reminder.maintenance_name,
                maintenanceType: reminder.maintenance_type,
                frequencyType: reminder.frequency_type,
                frequencyValue: reminder.frequency_value,
              } : null,
              existingReminders: existingReminders ? reminder : null,
            },
            // Helper data for sorting
            _sortName: asset.name.toLowerCase(),
            groupIds: asset.group_ids,
          };
        });
    assets = sortBy(assets, '_sortName');
    return { list: assets };
  }
}
